"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var SelectionRangeType;
(function (SelectionRangeType) {
    SelectionRangeType[SelectionRangeType["Row"] = 0] = "Row";
    SelectionRangeType[SelectionRangeType["Col"] = 1] = "Col";
    SelectionRangeType[SelectionRangeType["Cell"] = 2] = "Cell";
    SelectionRangeType[SelectionRangeType["RowAndCol"] = 3] = "RowAndCol";
})(SelectionRangeType || (SelectionRangeType = {}));
var ZerothCell = {
    row: 0,
    col: 0
};
exports.isSelectionDataIndexBased = 
// tslint:disable-next-line:no-any
function (selectionData) {
    var indexBasedSelectionData = selectionData;
    return Object.keys(indexBasedSelectionData).length === 3 &&
        Array.isArray(indexBasedSelectionData.cellRanges) &&
        Array.isArray(indexBasedSelectionData.colRanges) &&
        Array.isArray(indexBasedSelectionData.rowRanges);
};
/**
 * The GridSelection class is meant to track the state of selected cells/rows/cols, provide descriptive methods
 * to modify the selection, and query methods to see if a cell/row/col is selected.  It does not handle any
 * eventing, as different consumers may want to respond to events in different way (e.g. clicking on a cell
 * selects the entire row vs. selects only the cell).
 */
var GridSelection = /** @class */ (function () {
    function GridSelection() {
        this.selection = {
            rowRanges: [],
            colRanges: [],
            cellRanges: []
        };
        this.anchorPoint = ZerothCell;
        this.lastSelectionRangeType = null;
    }
    /**
     * Setters
     */
    // Clears selection, but does not reset the anchor point
    GridSelection.prototype.clearSelection = function () {
        this.selection.rowRanges = [];
        this.selection.colRanges = [];
        this.selection.cellRanges = [];
        this.lastSelectionRangeType = null;
    };
    GridSelection.prototype.resetAnchorPoint = function () {
        this.anchorPoint = ZerothCell;
    };
    /**
     * This initializes the selection data, sets the last selection range type to null, and the anchor point
     * to the ZerothCell.
     */
    GridSelection.prototype.initializeSelection = function (selectionData) {
        this.selection = selectionData;
        this.lastSelectionRangeType = null;
        this.resetAnchorPoint();
    };
    /**
     * This does not add a selection, but merely sets the anchor point for ranged selections.
     */
    GridSelection.prototype.setAnchorPoint = function (anchorPoint) {
        this.anchorPoint = anchorPoint;
    };
    /**
     * Getters
     */
    GridSelection.prototype.getSelection = function () {
        return this.selection;
    };
    /**
     * Static Query Methods
     */
    GridSelection.isCellSelected = function (cell, selectedCells) {
        return GridSelection.isRowSelected(cell.row, selectedCells) ||
            GridSelection.isColSelected(cell.col, selectedCells) ||
            GridSelection.isWithinCellRanges(cell, selectedCells);
    };
    GridSelection.isRowSelected = function (row, selectedCells) {
        return GridSelection.isInNumRanges(row, selectedCells.rowRanges);
    };
    GridSelection.isColSelected = function (col, selectedCells) {
        return GridSelection.isInNumRanges(col, selectedCells.colRanges);
    };
    GridSelection.isInNumRanges = function (num, numRanges) {
        for (var i = 0; i < numRanges.length; i++) {
            if (GridSelection.isInNumRange(num, numRanges[i])) {
                return true;
            }
        }
        return false;
    };
    GridSelection.isInNumRange = function (num, numRange) {
        return GridSelection.isBetweenNumRange(num, numRange.begin, numRange.end);
    };
    GridSelection.isWithinCellRanges = function (cell, selectedCells) {
        for (var i = 0; i < selectedCells.cellRanges.length; i++) {
            if (GridSelection.isWithinCellRange(cell, selectedCells.cellRanges[i])) {
                return true;
            }
        }
        return false;
    };
    GridSelection.isWithinCellRange = function (cell, cellRange) {
        var withinCol = GridSelection.isBetweenNumRange(cell.col, cellRange.begin.col, cellRange.end.col);
        var withinRow = GridSelection.isBetweenNumRange(cell.row, cellRange.begin.row, cellRange.end.row);
        return withinCol && withinRow;
    };
    GridSelection.isBetweenNumRange = function (num, begin, end) {
        if (end === GridSelection.infinity) {
            return num >= begin;
        }
        return (num >= begin && num <= end) || (num >= end && num <= begin);
    };
    GridSelection.normalizeNumRange = function (numRange) {
        return numRange.begin <= numRange.end ? numRange : { begin: numRange.end, end: numRange.begin };
    };
    GridSelection.normalizeCellRange = function (cellRange) {
        var beginCell = cellRange.begin;
        var endCell = cellRange.end;
        if (beginCell.row <= endCell.row && beginCell.col <= endCell.col) {
            return cellRange;
        }
        else if (endCell.row <= beginCell.row && endCell.col <= beginCell.col) {
            return {
                begin: endCell,
                end: beginCell
            };
        }
        else if (beginCell.row > endCell.row && beginCell.col < endCell.col) {
            return {
                begin: {
                    row: endCell.row,
                    col: beginCell.col
                },
                end: {
                    row: beginCell.row,
                    col: endCell.col
                }
            };
        }
        return {
            begin: {
                row: beginCell.row,
                col: endCell.col
            },
            end: {
                row: endCell.row,
                col: beginCell.col
            }
        };
    };
    /**
     * Select actions
     */
    GridSelection.prototype.deselectRow = function (row) {
        this.removeRow(row);
        this.removeCellsFromRow(row);
    };
    GridSelection.prototype.deselectColumn = function (col) {
        this.removeColumn(col);
        this.removeCellsFromColumn(col);
    };
    GridSelection.prototype.deselectCell = function (cell) {
        if (GridSelection.isRowSelected(cell.row, this.selection)) {
            this.removeRow(cell.row);
            this.addCellRangesForRemovedRow(cell);
        }
        if (GridSelection.isColSelected(cell.col, this.selection)) {
            this.removeColumn(cell.col);
            this.addCellRangesForRemovedColumn(cell);
        }
        this.removeCell(cell);
    };
    /**
     * This happens typically when ctrl + pointerPressing.  It adds to the selected cells and the sets it as the
     * last selected cell.
     */
    GridSelection.prototype.inclusiveCellSelect = function (cell) {
        this.selection.cellRanges.push({ begin: cell, end: cell });
        this.lastSelectionRangeType = SelectionRangeType.Cell;
        this.setAnchorPointFromCell(cell);
    };
    /**
     * This happens typically when pointerPressing or navigating with arrow keys.  It clears the selected cells,
     * then adds the cell to selected cells, and then sets it as the last selected cell.
     */
    GridSelection.prototype.exclusiveCellSelect = function (cell) {
        this.clearSelection();
        this.inclusiveCellSelect(cell);
    };
    /**
     * This happens typically when ctrl + pointerPressing on the row header.  It adds to the selected rows and the sets it as the
     * last selected row.
     */
    GridSelection.prototype.inclusiveRowSelect = function (row) {
        this.selection.rowRanges.push({ begin: row, end: row });
        this.lastSelectionRangeType = SelectionRangeType.Row;
        this.setAnchorPointFromRow(row);
    };
    /**
     * Clears out any current selection and replace it with the new specified selection.
     */
    GridSelection.prototype.exclusiveRowsSelect = function (rows) {
        this.clearSelection();
        for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
            var row = rows_1[_i];
            this.inclusiveRowSelect(row);
        }
    };
    /**
     * This happens typically when pointerPressing on the row header.  It clears the selected cells, then adds the row to selected
     * rows, and then sets it as the last selected row.
     */
    GridSelection.prototype.exclusiveRowSelect = function (row) {
        this.clearSelection();
        this.inclusiveRowSelect(row);
    };
    /**
     * This method clears the selected cells, then adds the rowRange and colRange to the selected rows and cols.
     */
    GridSelection.prototype.selectRowAndColRange = function (rowRange, colRange) {
        this.clearSelection();
        this.selection.rowRanges.push(GridSelection.normalizeNumRange(rowRange));
        this.selection.colRanges.push(GridSelection.normalizeNumRange(colRange));
        this.lastSelectionRangeType = SelectionRangeType.RowAndCol;
    };
    /**
     * This happens typically when ctrl + pointerPressing on the col header.  It adds to the selected cols and the sets it as the
     * last selected col.
     */
    GridSelection.prototype.inclusiveColSelect = function (col) {
        this.selection.colRanges.push({ begin: col, end: col });
        this.lastSelectionRangeType = SelectionRangeType.Col;
        this.setAnchorPointFromCol(col);
    };
    /**
     * This happens typically when pointerPressing on the col header.  It clears the selected cells, then adds the col to selected
     * cols, and then sets it as the last selected col.
     */
    GridSelection.prototype.exclusiveColSelect = function (col) {
        this.clearSelection();
        this.inclusiveColSelect(col);
    };
    /**
     * Performs a range selection from the anchorPoint (typically last row/col/cell selection) to the given row.
     * For a drag selection, do a row/col/cell selection on the starting point then do a range selection.
     */
    GridSelection.prototype.rangeRowSelectFromAnchor = function (row) {
        this.removeLastSelectedRangeIfNeeded();
        var rowRange = GridSelection.normalizeNumRange({ begin: this.anchorPoint.row, end: row });
        this.selection.rowRanges.push(rowRange);
        this.lastSelectionRangeType = SelectionRangeType.Row;
    };
    /**
     * Performs a range selection from the anchorPoint (typically last row/col/cell selection) to the given coll.
     * For a drag selection, do a row/col/cell selection on the starting point then do a range selection.
     */
    GridSelection.prototype.rangeColSelectFromAnchor = function (col) {
        this.removeLastSelectedRangeIfNeeded();
        var colRange = GridSelection.normalizeNumRange({ begin: this.anchorPoint.col, end: col });
        this.selection.colRanges.push(colRange);
        this.lastSelectionRangeType = SelectionRangeType.Col;
    };
    /**
     * Performs a range selection from the anchorPoint (typically last row/col/cell selection) to the given cell.
     * For a drag selection, do a row/col/cell selection on the starting point then do a range selection.
     */
    GridSelection.prototype.rangeCellSelectFromAnchor = function (cell) {
        this.removeLastSelectedRangeIfNeeded();
        var cellRange = GridSelection.normalizeCellRange({ begin: this.anchorPoint, end: cell });
        this.selection.cellRanges.push(cellRange);
        this.lastSelectionRangeType = SelectionRangeType.Cell;
    };
    /**
     * This pops the last selection type from the selection.
     */
    GridSelection.prototype.removeLastSelectedRangeIfNeeded = function () {
        switch (this.lastSelectionRangeType) {
            case SelectionRangeType.Cell:
                this.selection.cellRanges.pop();
                break;
            case SelectionRangeType.Col:
                this.selection.colRanges.pop();
                break;
            case SelectionRangeType.Row:
                this.selection.rowRanges.pop();
                break;
            default:
                return;
        }
    };
    GridSelection.prototype.setAnchorPointFromCol = function (col) {
        this.anchorPoint = { row: 0, col: col };
    };
    GridSelection.prototype.setAnchorPointFromRow = function (row) {
        this.anchorPoint = { row: row, col: 0 };
    };
    GridSelection.prototype.setAnchorPointFromCell = function (cell) {
        this.anchorPoint = cell;
    };
    GridSelection.prototype.removeRow = function (row) {
        var trimmedRowRanges = [];
        this.selection.rowRanges.forEach(function (rowRange) {
            if (GridSelection.isInNumRange(row, rowRange)) {
                if (row - rowRange.begin > 0) {
                    trimmedRowRanges.push({ begin: rowRange.begin, end: row - 1 });
                }
                if (rowRange.end - row > 0) {
                    trimmedRowRanges.push({ begin: row + 1, end: rowRange.end });
                }
            }
            else {
                trimmedRowRanges.push(rowRange);
            }
        });
        this.selection.rowRanges = trimmedRowRanges;
    };
    GridSelection.prototype.removeColumn = function (column) {
        var trimmedColRanges = [];
        this.selection.colRanges.forEach(function (colRange) {
            if (GridSelection.isInNumRange(column, colRange)) {
                if (column - colRange.begin > 0) {
                    trimmedColRanges.push({ begin: colRange.begin, end: column - 1 });
                }
                if (colRange.end - column > 0) {
                    trimmedColRanges.push({ begin: column + 1, end: colRange.end });
                }
            }
            else {
                trimmedColRanges.push(colRange);
            }
        });
        this.selection.colRanges = trimmedColRanges;
    };
    GridSelection.prototype.removeCell = function (cell) {
        var trimmedCellRanges = [];
        this.selection.cellRanges.forEach(function (cellRange) {
            if (GridSelection.isWithinCellRange(cell, cellRange)) {
                var beginCell = cellRange.begin;
                var endCell = cellRange.end;
                if (cell.row - beginCell.row > 0) {
                    trimmedCellRanges.push({
                        begin: beginCell,
                        end: { row: cell.row - 1, col: endCell.col }
                    });
                }
                if (endCell.row === GridSelection.infinity || endCell.row - cell.row > 0) {
                    trimmedCellRanges.push({
                        begin: { row: cell.row + 1, col: beginCell.col },
                        end: endCell
                    });
                }
                if (cell.col - beginCell.col > 0) {
                    trimmedCellRanges.push({
                        begin: { row: cell.row, col: beginCell.col },
                        end: { row: cell.row, col: cell.col - 1 }
                    });
                }
                if (endCell.col === GridSelection.infinity || endCell.col - cell.col > 0) {
                    trimmedCellRanges.push({
                        begin: { row: cell.row, col: cell.col + 1 },
                        end: { row: cell.row, col: endCell.col }
                    });
                }
            }
            else {
                trimmedCellRanges.push(cellRange);
            }
        });
        this.selection.cellRanges = trimmedCellRanges;
    };
    GridSelection.prototype.removeCellsFromRow = function (row) {
        var trimmedCellRanges = [];
        this.selection.cellRanges.forEach(function (cellRange) {
            if (GridSelection.isBetweenNumRange(row, cellRange.begin.row, cellRange.end.row)) {
                var beginCell = cellRange.begin;
                var endCell = cellRange.end;
                if (row - beginCell.row > 0) {
                    trimmedCellRanges.push({
                        begin: beginCell,
                        end: { row: row - 1, col: endCell.col }
                    });
                }
                if (endCell.row === GridSelection.infinity || endCell.row - row > 0) {
                    trimmedCellRanges.push({
                        begin: { row: row + 1, col: beginCell.col },
                        end: endCell
                    });
                }
            }
            else {
                trimmedCellRanges.push(cellRange);
            }
        });
        this.selection.cellRanges = trimmedCellRanges;
    };
    GridSelection.prototype.removeCellsFromColumn = function (col) {
        var trimmedCellRanges = [];
        this.selection.cellRanges.forEach(function (cellRange) {
            if (GridSelection.isBetweenNumRange(col, cellRange.begin.col, cellRange.end.col)) {
                var beginCell = cellRange.begin;
                var endCell = cellRange.end;
                if (col - beginCell.col > 0) {
                    trimmedCellRanges.push({
                        begin: { row: beginCell.row, col: beginCell.col },
                        end: { row: endCell.row, col: col - 1 }
                    });
                }
                if (endCell.col === GridSelection.infinity || endCell.col - col > 0) {
                    trimmedCellRanges.push({
                        begin: { row: beginCell.row, col: col + 1 },
                        end: { row: endCell.row, col: endCell.col }
                    });
                }
            }
            else {
                trimmedCellRanges.push(cellRange);
            }
        });
        this.selection.cellRanges = trimmedCellRanges;
    };
    GridSelection.prototype.addCellRangesForRemovedRow = function (cell) {
        if (cell.col > 0) {
            this.selection.cellRanges.push({
                begin: { row: cell.row, col: 0 },
                end: { row: cell.row, col: cell.col - 1 }
            });
        }
        this.selection.cellRanges.push({
            begin: { row: cell.row, col: cell.col + 1 },
            end: { row: cell.row, col: GridSelection.infinity }
        });
    };
    GridSelection.prototype.addCellRangesForRemovedColumn = function (cell) {
        if (cell.row > 0) {
            this.selection.cellRanges.push({
                begin: { row: 0, col: cell.col },
                end: { row: cell.row - 1, col: cell.col }
            });
        }
        this.selection.cellRanges.push({
            begin: { row: cell.row + 1, col: cell.col },
            end: { row: GridSelection.infinity, col: cell.col }
        });
    };
    GridSelection.infinity = -1;
    return GridSelection;
}());
exports.GridSelection = GridSelection;
//# sourceMappingURL=GridSelection.js.map